// src/pages/Accounting/Reconcile/Session/HowItWorksTour.tsx

import type React from "react";
import { useState } from "react";
import { X } from "lucide-react";
import { PALETTE } from "../constants";

interface HowItWorksTourProps {
  onClose: () => void;
}

interface Step {
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    title: "Get the difference to $0.00",
    body: "As you clear each transaction, this updates. When Vela plus you have cleared enough, this hits $0.00 and you're ready to sign off.",
  },
  {
    title: "Collapse to focus",
    body: "Need more room? Hide the balance section with the chevron to focus on the transaction list.",
  },
  {
    title: "Filter the list",
    body: "Switch tabs to focus on Payments only, Deposits only, or everything. The funnel icon opens full filters: cleared status, type, payee, date range.",
  },
  {
    title: "Vela pre-matched these",
    body: "The V means Vela pre-matched this one. Rows with an amber ring need your attention. Click the circle to clear it.",
  },
  {
    title: "Edit a transaction",
    body: "Click any row to open it, change the matched receipt, category, or account, and save.",
  },
];

export function HowItWorksTour({ onClose }: HowItWorksTourProps) {
  const [step, setStep] = useState(0);
  const total = STEPS.length;
  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === total - 1;

  const back = () => setStep((s) => Math.max(0, s - 1));
  const next = () => {
    if (isLast) onClose();
    else setStep((s) => Math.min(total - 1, s + 1));
  };

  return (
    <>
      <div style={overlayStyle} onClick={onClose} />
      <div style={popStyle}>
        <div style={headStyle}>
          <div style={titleStyle}>{current.title}</div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close tour">
            <X size={18} />
          </button>
        </div>
        <div style={bodyStyle}>{current.body}</div>
        <div style={footStyle}>
          <div style={progressStyle}>{step + 1} of {total}</div>
          <div style={navStyle}>
            {!isFirst && <button type="button" onClick={back} style={backBtnStyle}>Back</button>}
            <button type="button" onClick={next} style={nextBtnStyle}>{isLast ? "I'm done!" : "Next"}</button>
          </div>
        </div>
      </div>
    </>
  );
}

const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(14, 26, 26, 0.55)", zIndex: 100 };
const popStyle: React.CSSProperties = { position: "fixed", top: 180, right: 32, zIndex: 102, background: "#fff", width: 320, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" };
const headStyle: React.CSSProperties = { padding: "16px 20px 8px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const titleStyle: React.CSSProperties = { fontSize: 16, fontWeight: 700, color: PALETTE.ink, margin: 0 };
const closeBtnStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.ink3, cursor: "pointer", padding: 0, display: "flex", alignItems: "center" };
const bodyStyle: React.CSSProperties = { padding: "0 20px 14px", fontSize: 13.5, color: PALETTE.ink2, fontWeight: 500, lineHeight: 1.55 };
const footStyle: React.CSSProperties = { padding: "11px 20px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${PALETTE.lineSoft}` };
const progressStyle: React.CSSProperties = { fontSize: 12, color: PALETTE.ink3, fontWeight: 500 };
const navStyle: React.CSSProperties = { display: "flex", gap: 8 };
const backBtnStyle: React.CSSProperties = { padding: "7px 14px", fontSize: 12.5, fontWeight: 600, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", border: 0, background: "transparent", color: PALETTE.ink2 };
const nextBtnStyle: React.CSSProperties = { padding: "7px 14px", fontSize: 12.5, fontWeight: 600, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", border: 0, background: PALETTE.ink2, color: "#fff" };